import * as React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useGetSingleStudentQuery } from "@/store/studentApi";
import { useSelector } from "react-redux";
import { RootState } from "@/types";
import { Link } from "react-router-dom";
import ProjectView from "./Project/ProjectView";
import { useGetAllNotificationQuery } from "../store/notificationApi";
import { useGetAttendenceDetailsQuery } from "../store/attendenceApi";
import AttendancePerformanceChart from "./ui/AttendancePerformanceChart";
import { useGetRankingQuery } from "@/store/ranking/rankingApi";
import { CheckCircle2, XCircle } from "lucide-react";
import { Badge } from "./ui/badge";

const StudentDashboardUi = () => {
  const { user } = useSelector((state: RootState) => state.user);

  const { data: student } = useGetSingleStudentQuery(user?._id);
  const { data: notifications } = useGetAllNotificationQuery();
  const { data: allAttendance } = useGetAttendenceDetailsQuery(user?._id);

  const { data: ranking } = useGetRankingQuery({
    studentId: user._id,
    batchCode: user.batch._id,
  });

  // Notifications
  const filteredNotifications = React.useMemo(() => {
    if (!notifications || !student) return [];
    return notifications?.find(
      (notification) => notification?.batch === student?.batch,
    );
  }, [notifications, student]);

  // ---- Extract Ranking Data From API ----
  const totalScore = ranking?.data?.ranking?.totalScore || 0;
  const rankPosition = ranking?.data?.ranking?.rank || "-";
  const totalStudents = ranking?.data?.totalStudents || "-";
  const percentile = ranking?.data?.percentile || "-";

  const interviewScore = ranking?.data?.ranking?.interviewScore || 0;
  const assignmentScore = ranking?.data?.ranking?.assignmentScore || 0;
  const taskScore = ranking?.data?.ranking?.taskScore || 0;

  // ---- Grade Logic ----
  const getRankGrade = (score) => {
    if (!score) return "-";
    if (score >= 90) return "S";
    if (score >= 80) return "A";
    if (score >= 70) return "B";
    if (score >= 60) return "C";
    if (score >= 50) return "D";
    return "F";
  };

  const rankGrade = getRankGrade(totalScore);

  // ---- Eligibility Logic ----
  const isEligibleForFinalInterview = React.useMemo(() => {
    return interviewScore >= 80 && assignmentScore >= 80 && taskScore >= 80;
  }, [interviewScore, assignmentScore, taskScore]);

  return (
    <div className="p-2 sm:p-4 max-w-7xl mx-auto bg-background">
      {/* Notifications */}
      {notifications && filteredNotifications?.message && (
        <div className="flex bg-green-200 px-5 rounded-xl mb-3 py-2">
          <strong className="mr-2">Note:</strong>
          <marquee>{filteredNotifications?.message}</marquee>
        </div>
      )}

      <Card
        className="bg-gradient-to-r from-green-600 to-green-500 
                 dark:from-green-800 dark:to-green-700 
                 text-white mb-6 border-0"
      >
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-4">
            {/* Left Section */}
            <div className="flex-1">
              <h2 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">
                WELCOME BACK, {student?.fname?.toUpperCase()}
              </h2>

              {/* Stats Row */}
              <div className="flex flex-wrap items-center mb-3 gap-2 sm:gap-3 text-xs sm:text-sm">
                <h1 className="text-base sm:text-lg font-semibold">
                  Ranking :
                </h1>
                <span className="space-x-2 sm:space-x-3 flex flex-wrap">
                  <span className="text-white text-sm sm:text-base font-bold">
                    {rankGrade}
                  </span>
                  <span className="text-white/90 dark:text-white/80">
                    {Math.round(totalScore)}%
                  </span>
                  <span className="text-white/80 dark:text-white/70">
                    #{rankPosition} of {totalStudents}
                  </span>
                  <span className="text-white/80 dark:text-white/70">
                    {percentile} percentile
                  </span>
                </span>
              </div>

              {/* Eligibility Section */}
              <div className="bg-white/15 dark:bg-white/10 rounded-lg p-2.5 sm:p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-xs sm:text-sm text-white/95">
                    Final Interview
                  </span>
                  <Badge
                    className={`${
                      isEligibleForFinalInterview
                        ? "bg-green-800 hover:bg-green-800 dark:bg-green-900 dark:hover:bg-green-900"
                        : "bg-red-600 hover:bg-red-600 dark:bg-red-700 dark:hover:bg-red-700"
                    } text-white text-[10px] sm:text-xs`}
                  >
                    {isEligibleForFinalInterview ? "ELIGIBLE" : "NOT ELIGIBLE"}
                  </Badge>
                </div>

                {!isEligibleForFinalInterview && (
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 text-[11px] sm:text-xs text-white/90 dark:text-white/80">
                    <div className="flex items-center gap-1">
                      {interviewScore >= 80 ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-200" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5 text-red-300" />
                      )}
                      <span>
                        Interview:{" "}
                        <span className="font-bold text-white">
                          {interviewScore}%
                        </span>
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      {assignmentScore >= 80 ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-200" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5 text-red-300" />
                      )}
                      <span>
                        Assignment:{" "}
                        <span className="font-bold text-white">
                          {assignmentScore}%
                        </span>
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      {taskScore >= 80 ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-200" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5 text-red-300" />
                      )}
                      <span>
                        Tasks:{" "}
                        <span className="font-bold text-white">
                          {taskScore}%
                        </span>
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <a href={`${student?.username}`} className="inline-block mt-3">
                <button
                  className="bg-white text-green-700 
                             dark:bg-gray-900 dark:text-green-400
                             font-semibold px-4 py-1.5 rounded-lg 
                             hover:bg-green-50 dark:hover:bg-gray-800 
                             transition-all text-xs sm:text-sm"
                >
                  View Profile
                </button>
              </a>
            </div>

            {/* Profile Avatar */}
            {/* <div
              className="shrink-0 self-center sm:self-auto
                   w-16 h-16 sm:w-20 sm:h-20
                   bg-white/20 dark:bg-white/10
                   rounded-full
                   flex items-center justify-center
                   text-xl sm:text-3xl font-bold
                   border-2 border-white/30 dark:border-white/20"
            >
              {student?.profile ? (
                <img
                  className="w-full h-full rounded-full object-cover"
                  src={student.profile}
                  alt="Profile"
                />
              ) : (
                <span className="text-white">
                  {student?.fname?.charAt(0)}
                  {student?.lname?.charAt(0)}
                </span>
              )}
            </div> */}
          </div>
        </CardContent>
      </Card>

      {/* Projects */}
      <div className="grid grid-cols-1 mt-6">
        <Card>
          <CardContent>
            <ProjectView />
          </CardContent>
        </Card>
      </div>

      {/* Performance Chart */}
      {/* <Card className="mb-6 p-0"> */}
        {/* <CardContent className=""> */}
          <AttendancePerformanceChart attendanceData={allAttendance} />
        {/* </CardContent> */}
      {/* </Card> */}
    </div>
  );
};

export default StudentDashboardUi;
